// @flow
import * as React from 'react';
import { format as formatDate } from 'date-fns';
import { sort as sortFn } from 'utils/functional';
import type { ActionType, ActionSubType, VolopImportedType } from 'api/volops';

export const STRIPE_ERROR_CODES_MESSAGE_MAP = {
  invalid_number: /* TRANSLATORS: credit card form error message */ getText(
    'The card number is not a valid credit card number.'
  ),
  invalid_expiry_month: /* TRANSLATORS: credit card form error message */ getText(
    "The card's expiration month is invalid."
  ),
  invalid_expiry_year: /* TRANSLATORS: credit card form error message */ getText(
    "The card's expiration year is invalid."
  ),
  invalid_cvc: /* TRANSLATORS: credit card form error message */ getText(
    "The card's security code is invalid."
  ),
  incorrect_number: /* TRANSLATORS: credit card form error message */ getText(
    'The card number is incorrect.'
  ),
  expired_card: /* TRANSLATORS: credit card form error message */ getText(
    'The card has expired.'
  ),
  incorrect_cvc: /* TRANSLATORS: credit card form error message */ getText(
    "The card's security code is incorrect."
  ),
  incorrect_zip: /* TRANSLATORS: credit card form error message */ getText(
    "The card's zip code failed validation."
  ),
  card_declined: /* TRANSLATORS: credit card form error message */ getText(
    'The card was declined.'
  ),
  missing: /* TRANSLATORS: credit card form error message */ getText(
    'There is no card on a customer that is being charged.'
  ),
  processing_error: /* TRANSLATORS: credit card form error message */ getText(
    'An error occurred while processing the card.'
  ),
  unexpected: /* TRANSLATORS: credit card form error message */ getText(
    'An unexpected error occured.'
  )
};

export const HOME_PAGE_SLUGS = {
  ALL: {
    en: 'all',
    es: 'todas',
    pt: 'tudo'
  },
  JOB: {
    en: 'jobs',
    es: 'empleos',
    pt: 'empregos'
  },
  INTERNSHIP: {
    en: 'internships',
    es: 'pasantias',
    pt: 'estagios'
  },
  VOLOP: {
    en: 'volunteer',
    es: 'voluntariado',
    pt: 'voluntariado'
  },
  ORG: {
    en: 'organizations',
    es: 'organizaciones',
    pt: 'organizacoes'
  },
  GROUP: {
    en: 'groups',
    es: 'grupos',
    pt: 'coletivos'
  },
  BLOG: {
    en: 'blog',
    es: 'blog',
    pt: 'blog'
  }
};

export const LISTING_TYPE_URL_SEGMENT_MAP = {
  ALL: 'all',
  JOB: 'jobs',
  INTERNSHIP: 'internships',
  VOLOP: 'volops',
  ORG: 'organizations',
  GROUP: 'groups',
  IDEALIST_DAY_EVENT: 'idealist-day-events',
  GRAD_PROGRAM: 'grad-programs',
  USER: 'users'
};

export const LISTING_TYPE_URL_SEGMENT_MAP_SUPPORT_CASE = {
  ...LISTING_TYPE_URL_SEGMENT_MAP,
  IDEALIST_DAYS_MAP_PIN: 'idealist-days-map-pins'
};

export const STATUS_URL_SEGMENT_MAP = {
  ARCHIVED: 'expired',
  DRAFT: 'draft',
  PUBLISHED: 'published',
  HIDDEN: 'hidden',
  FROZEN: 'frozen',
  PULLED: 'pulled'
};

export const ADDRESS_TYPE = {
  FULL: 'full',
  CITY_ONLY: 'cityOnly'
};

export const OBJECT_TYPE_URL_SEGMENT_MAP = {
  ...LISTING_TYPE_URL_SEGMENT_MAP,
  INVOICE: 'invoices',
  USER: 'users',
  PAYMENT: 'payments',
  CREDIT_PACK: 'credits',
  DONATION: 'donations'
};

export const LISTING_TYPE_HUMAN_NAME_MAP = {
  JOB: getText('job'),
  INTERNSHIP: getText('internship'),
  VOLOP: getText('volunteer opportunity'),
  ORG: getText('organization'),
  GROUP: getText('group'),
  IDEALIST_DAY_EVENT: getText('Idealist Day'),
  GRAD_PROGRAM: getText('graduate program'),
  IDEALIST_DAY_MAP: getText('event'),
  IDEALIST_DAYS_MAP_PIN: getText('action'),
  USER: getText('user')
};

export const LISTING_TYPE_HUMAN_NAME_MAP_POST = {
  ALL: getText('Post a listing'),
  JOB: getText('Post a job'),
  INTERNSHIP: getText('Post an internship'),
  VOLOP: getText('Post an opportunity'),
  ORG: getText('Add your organization'),
  GROUP: getText('Add your group'),
  IDEALIST_DAY_EVENT: getText('Post an activity'),
  GRAD_PROGRAM: getText('Add your program')
};

export const LISTING_TYPE_HUMAN_NAME_MAP_TITLE = {
  JOB: getText('Job'),
  INTERNSHIP: getText('Internship'),
  VOLOP: getText('Volunteer Opportunity'),
  ORG: getText('Organization'),
  GROUP: getText('Mutual Aid Group'),
  IDEALIST_DAY_EVENT: getText('Idealist Day'),
  GRAD_PROGRAM: getText('Graduate Program'),
  IDEALIST_DAYS_MAP_PIN: getText('Action'),
  USER: getText('User')
};

export const LISTING_TYPE_HUMAN_NAME_MAP_COUNTS = {
  ...LISTING_TYPE_HUMAN_NAME_MAP_TITLE,
  VOLOP: getText('Volunteer Opportunity'),
  GRAD_PROGRAM: getText('Grad Program')
};

export const LISTING_TYPE_HUMAN_NAME_MAP_SEARCH = {
  ALL: getText('Everything'),
  JOB: getText('Jobs'),
  INTERNSHIP: getText('Internships'),
  VOLOP: getText('Volunteer +'),
  ORG: getText('Organizations'),
  IDEALIST_DAY_EVENT: getText('Idealist Days'),
  GROUP: getText('Mutual Aid Groups'),
  GRAD_PROGRAM: getText('Grad Programs'),
  USER: getText('Users'),
  INVOICE: getText('Invoices'),
  PAYMENT: getText('Payments'),
  DONATION: getText('Donations'),
  CMS_PAGE: getText('Articles')
};

export const OBJECT_TYPE_HUMAN_NAME_MAP = {
  ...LISTING_TYPE_HUMAN_NAME_MAP,
  INVOICE: getText('invoice'),
  USER: getText('user'),
  PAYMENT: getText('payment'),
  DONATION: getText('donation')
};

export const OBJECT_TYPE_HUMAN_NAME_MAP_TITLE = {
  ...LISTING_TYPE_HUMAN_NAME_MAP_TITLE,
  INVOICE: getText('Invoice'),
  USER: getText('User'),
  PAYMENT: getText('Payment'),
  DONATION: getText('Donations')
};

export const LISTING_TYPE_PLURAL_HUMAN_NAME_MAP = {
  ALL: getText('all listings'),
  JOB: getText('jobs'),
  INTERNSHIP: getText('internships'),
  VOLOP: getText('volunteer opportunities'),
  ORG: getText('organizations'),
  IDEALIST_DAY_EVENT: getText('Idealist Days'),
  GROUP: getText('groups'),
  GRAD_PROGRAM: getText('graduate programs')
};

export const OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP,
  USER: getText('users'),
  INVOICE: getText('invoices'),
  PAYMENT: getText('payments'),
  DONATION: getText('donations')
};

export const LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE = {
  JOB: getText('Jobs'),
  INTERNSHIP: getText('Internships'),
  VOLOP: getText('Volunteer +'),
  ORG: getText('Organizations'),
  GROUP: getText('Mutual Aid Groups'),
  IDEALIST_DAY_EVENT: getText('Idealist Days'),
  GRAD_PROGRAM: getText('Graduate Programs')
};

// @TODO Merge with another constant that doesn't have VOLOPS after launch
export const LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_DEFAULT_SEARCH = {
  JOB: getText('Jobs'),
  INTERNSHIP: getText('Internships'),
  ORG: getText('Organizations'),
  GROUP: getText('Mutual Aid Groups'),
  IDEALIST_DAY_EVENT: getText('Idealist Days'),
  GRAD_PROGRAM: getText('Graduate Programs'),
  VOLOP: getText('Volunteer +')
};

export const LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_COUNTS = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE,
  VOLOP: getText('Volunteer Opportunities'),
  GRAD_PROGRAM: getText('Grad Programs')
};

export const OBJECT_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP,
  USER: getText('Users'),
  INVOICE: getText('Invoices'),
  PAYMENT: getText('Payments'),
  DONATION: getText('Donations')
};

export const STATUSES = {
  DELETED: /* Status */ getText('Deleted'),
  PUBLISHED: /* Status */ getText('Published'),
  DRAFT: /* Status */ getText('Draft'),
  ARCHIVED: /* Status */ getText('Expired'),
  FROZEN: /* Status */ getText('Frozen'),
  HIDDEN: /* Status */ getText('Hidden'),
  CHECKOUT: /* Status */ getText('In Cart'),
  INVOICE: /* Status, awaiting payment */ getText('Invoiced'),
  PULLED: /* Status */ getText('Pulled from site'),
  APPROVED: /* Status */ getText('Approved'),
  PENDING: /* Status */ getText('Pending'),
  REJECTED: /* Status */ getText('Rejected'),
  SPAM: /* Status */ getText('Spam')
};

export const STATUSES_FOR_FILTERING: Array<{
  id: string,
  name: string
}> = Object.keys(STATUSES).reduce((acc, cur) => {
  acc.push({ id: cur, name: STATUSES[cur] });
  return acc;
}, []);

export const ORG_TYPES: $ReadOnlyArray<
  $ReadOnly<{ id: string, name: string }>
> = [
  { id: 'NONPROFIT', name: /* TRANSLATORS: org type */ getText('Nonprofit') },
  { id: 'GOVERNMENT', name: /* TRANSLATORS: org type */ getText('Government') },
  {
    id: 'SOCIAL_ENTERPRISE',
    name: /* TRANSLATORS: org type */ getText('Social Enterprise')
  },
  { id: 'RECRUITER', name: /* TRANSLATORS: org type */ getText('Recruiter') },
  { id: 'CONSULTANT', name: /* TRANSLATORS: org type */ getText('Consultant') }
];

export const ORG_TYPES_ALLOW_TO_POST_VOLOPS: $ReadOnlyArray<
  $ReadOnly<{
    id: string,
    name: string
  }>
> = ORG_TYPES.filter(type => ['NONPROFIT', 'GOVERNMENT'].includes(type.id));

export const LOCALES = [
  { id: 'en', name: 'English' },
  { id: 'es', name: 'Español' },
  { id: 'pt', name: 'Português' }
];

const kmText = /* TRANSLATORS: abbreviation for kilometers */ getText('km');

const autoSearchRadius = {
  id: 'auto',
  name:
    /* TRANSLATORS: option in search radius selector to automatically determine the radius */
    getText('Default Radius')
};

export const SEARCH_RADIUSES = {
  en: [
    autoSearchRadius,
    { id: '8000', name: '5 miles' },
    { id: '16000', name: '10 miles' },
    { id: '24000', name: '15 miles' },
    { id: '40000', name: '25 miles' },
    { id: '100000', name: '60 miles' },
    { id: '160000', name: '100 miles' }
  ],
  es: [
    autoSearchRadius,
    { id: '5000', name: `5 ${kmText}` },
    { id: '10000', name: `10 ${kmText}` },
    { id: '15000', name: `15 ${kmText}` },
    { id: '25000', name: `25 ${kmText}` },
    { id: '60000', name: `60 ${kmText}` },
    { id: '100000', name: `100 ${kmText}` }
  ],
  pt: [
    autoSearchRadius,
    { id: '5000', name: `5 ${kmText}` },
    { id: '10000', name: `10 ${kmText}` },
    { id: '15000', name: `15 ${kmText}` },
    { id: '25000', name: `25 ${kmText}` },
    { id: '60000', name: `60 ${kmText}` },
    { id: '100000', name: `100 ${kmText}` }
  ]
};

const DEFAULT_SEARCH_RADIUS_BY_LOCALE = {
  en: {
    relevance: 'auto',
    newest: '40000'
  },
  es: {
    relevance: 'auto',
    newest: '60000'
  },
  pt: {
    relevance: 'auto',
    newest: '60000'
  }
};

export const getDefaultSearchRadius = (sort: string = 'relevance') =>
  DEFAULT_SEARCH_RADIUS_BY_LOCALE[USER_LOCALE][sort];

export const DEFAULT_SEARCH_RADIUS = getDefaultSearchRadius();

export const OBJECT_TYPES = [
  { id: 'ORG', name: /* TRANSLATORS: object type */ getText('Organization') },
  { id: 'JOB', name: /* TRANSLATORS: object type */ getText('Job') },
  {
    id: 'INTERNSHIP',
    name: /* TRANSLATORS: object type */ getText('Internship')
  },
  {
    id: 'VOLOP',
    name: /* TRANSLATORS: object type */ getText('Volunteer Opportunity')
  }
];

export const LINE_ITEM_TYPES = [
  { id: 'JOB', name: /* TRANSLATORS: line item type */ getText('Job Listing') },
  {
    id: 'INTERNSHIP',
    name: /* TRANSLATORS: line item type */ getText('Internship Listing')
  },
  {
    id: 'CREDITS',
    name: /* TRANSLATORS: line item type */ getText('Credit Pack')
  },
  {
    id: 'JOB_PACK_10',
    name: /* TRANSLATORS: line item type */ getText('10 Job Pack')
  },
  {
    id: 'JOB_PACK_20',
    name: /* TRANSLATORS: line item type */ getText('20 Job Pack')
  },
  {
    id: 'JOB_PACK_25',
    name: /* TRANSLATORS: line item type */ getText('25 Job Pack')
  },
  {
    id: 'JOB_PACK_30',
    name: /* TRANSLATORS: line item type */ getText('30 Job Pack')
  },
  {
    id: 'JOB_PACK_50',
    name: /* TRANSLATORS: line item type */ getText('50 Job Pack')
  },
  {
    id: 'JOB_PACK_100',
    name: /* TRANSLATORS: line item type */ getText('100 Job Pack')
  },
  {
    id: 'JOB_PACK_500',
    name: /* TRANSLATORS: line item type */ getText('500 Job Pack')
  },
  {
    id: 'INTERNSHIP_PACK_10',
    name: /* TRANSLATORS: line item type */ getText('10 Internship Pack')
  },
  {
    id: 'FAIR',
    name: /* TRANSLATORS: line item type */ getText('Grad Fair Exhibitor')
  },
  {
    id: 'GRAD_PROGRAM',
    name: /* TRANSLATORS: line item type */ getText('Grad Program')
  },
  {
    id: 'VOLOP',
    name: /* TRANSLATORS: line item type */ getText('Volunteer Opportunity')
  },
  {
    id: 'DONATION',
    name: /* TRANSLATORS: line item type */ getText('Donation')
  }
];

// how many days a listing stays published
export const ENTITLEMENTS = [
  { id: 'JOB', name: 30 },
  { id: 'INTERNSHIP', name: 30 },
  { id: 'VOLOP', name: 180 }
];

export const PROFESSIONAL_LEVELS = [
  {
    id: 'ENTRY_LEVEL',
    name: /* TRANSLATORS: professional level */ getText('Entry level')
  },
  {
    id: 'PROFESSIONAL',
    name: /* TRANSLATORS: professional level */ getText('Professional')
  },
  {
    id: 'MANAGERIAL',
    name: /* TRANSLATORS: professional level */ getText('Managerial')
  },
  {
    id: 'EXECUTIVE',
    name: /* TRANSLATORS: professional level */ getText('Executive')
  },
  {
    id: 'NONE',
    name: /* TRANSLATORS: professional level */ getText('None specified')
  }
];

export const EDUCATION_LEVELS = [
  {
    id: 'NO_REQUIREMENT',
    name: /* TRANSLATORS: education level */ getText('No Education Requirement')
  },
  {
    id: 'HIGH_SCHOOL',
    name: /* TRANSLATORS: education level */ getText(
      'High School Diploma Required'
    )
  },
  {
    id: 'TWO_YEAR_DEGREE',
    name: /* TRANSLATORS: education level */ getText('2-Year Degree Required')
  },
  {
    id: 'FOUR_YEAR_DEGREE',
    name: /* TRANSLATORS: education level */ getText('4-Year Degree Required')
  },
  {
    id: 'MASTERS_DEGREE',
    name: /* TRANSLATORS: education level */ getText("Master's Degree Required")
  },
  {
    id: 'JD',
    name: /* TRANSLATORS: education level */ getText('J.D. Required')
  },
  {
    id: 'MD',
    name: /* TRANSLATORS: education level */ getText('M.D. Required')
  },
  {
    id: 'PHD',
    name: /* TRANSLATORS: education level */ getText('Doctorate (PhD) Required')
  },
  {
    id: 'OTHER',
    name: /* TRANSLATORS: education level */ getText(
      'Other Education Requirement'
    )
  }
];

export const PAYMENT_LEVELS = [
  {
    id: 'HOURLY',
    name: /* TRANSLATORS: payment level */ getText('Hourly')
  },
  {
    id: 'SALARY',
    name: /* TRANSLATORS: payment level */ getText('Salary')
  },
  {
    id: 'STIPEND',
    name: /* TRANSLATORS: payment level */ getText('Stipend')
  }
];

export const TIMES_OF_DAY = [
  { id: 'NONE', name: '' },
  {
    id: 'WEEKDAY_MORNINGS',
    name: /* TRANSLATORS: time of day */ getText('Weekday Mornings')
  },
  {
    id: 'WEEKDAY_AFTERNOONS',
    name: /* TRANSLATORS: time of day */ getText('Weekday Afternoons')
  },
  {
    id: 'WEEKDAY_EVENINGS',
    name: /* TRANSLATORS: time of day */ getText('Weekday Evenings')
  },
  { id: 'WEEKDAYS', name: /* TRANSLATORS: time of day */ getText('Weekdays') }
];

export const TIMES_OF_DAY_VOLOPS = [
  { id: 'WEEKDAYS', name: /* TRANSLATORS: time of day */ getText('Weekdays') },
  { id: 'WEEKENDS', name: /* TRANSLATORS: time of day */ getText('Weekends') }
];

export const EXPECTED_TIMES_RECURRING = [
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('A few hours per week'),
    id: 'FEW_HOURS_WEEK'
  },
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('A few hours per month'),
    id: 'FEW_HOURS_MONTH'
  },
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('Part time (10-30 hrs/wk)'),
    id: 'PART_TIME'
  },
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('Full time (30+ hrs/wk)'),
    id: 'FULL_TIME'
  }
];

export const EXPECTED_TIMES_ONCE = [
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('A few minutes'),
    id: 'FEW_MINUTES'
  },
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('A few hours'),
    id: 'FEW_HOURS'
  },
  {
    /* TRANSLATORS: Form option for volop time commitment */
    name: getText('A few days'),
    id: 'FEW_DAYS'
  }
];

export const EXPECTED_TIMES = [
  { id: 'NONE', name: '' },
  {
    id: 'FEW_HOURS_MONTH',
    name: /* TRANSLATORS: expected time */ getText('A few hours per month')
  },
  {
    id: 'FEW_HOURS_WEEK',
    name: /* TRANSLATORS: expected time */ getText('A few hours per week')
  },
  {
    id: 'PART_TIME',
    name: /* TRANSLATORS: expected time */ getText('Part time (10-30 hrs/wk)')
  },
  {
    id: 'FULL_TIME',
    name: /* TRANSLATORS: expected time */ getText('Full time (30+ hrs/wk)')
  }
];

export const DURATIONS = [
  { id: 'NONE', name: '' },
  {
    id: 'ONE_TIME',
    name: /* TRANSLATORS: duration */ getText('One-time only')
  },
  {
    id: 'SHORT_TERM',
    name: /* TRANSLATORS: duration */ getText('Short-Term (few weeks/months)')
  },
  {
    id: 'LONG_TERM',
    name: /* TRANSLATORS: duration */ getText('Long-Term (6 months-1 year)')
  }
];

export const ACTION_TYPE: Array<{
  id: ActionType | 'NONE',
  name: string
}> = [
  {
    id: 'NONE',
    name: ''
  },
  {
    id: 'VOLOP',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Volunteer Opportunity')
  },
  {
    id: 'EVENT',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Event')
  },
  {
    id: 'LOAN',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Loan')
  },
  {
    id: 'DONATION',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Donation')
  },
  {
    id: 'PETITION',
    name: /* TRANSLATORS: Action Opp type */ getText('Petition')
  },
  {
    id: 'OTHER',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Other Opportunity')
  }
];

export const ACTION_TYPE_MAP_TITLE = {
  NONE: '',
  VOLOP: /* TRANSLATORS: "Volunteer +" type */ getText('Volunteer Opportunity'),
  EVENT: /* TRANSLATORS: "Volunteer +" type */ getText('Event'),
  LOAN: /* TRANSLATORS: "Volunteer +" type */ getText('Loan'),
  DONATION: /* TRANSLATORS: "Volunteer +" type */ getText('Donation'),
  OTHER: /* TRANSLATORS: "Volunteer +" type */ getText('Other Opportunity'),
  PETITION: /* TRANSLATORS: "Volunteer +" type */ getText('Petition')
};

export const ACTION_TYPE_PLURAL: Array<{
  id: ActionType | 'NONE',
  name: string
}> = [
  {
    id: 'NONE',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('All')
  },
  {
    id: 'VOLOP',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Volunteering')
  },
  {
    id: 'EVENT',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Events')
  },
  {
    id: 'LOAN',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Loans')
  },
  {
    id: 'DONATION',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Donations')
  },
  {
    id: 'PETITION',
    name: /* TRANSLATORS: Volunteer +" type */ getText('Petitions')
  }
];

export const ACTION_SUB_TYPE: Array<{
  id: ActionSubType | 'NONE',
  name: string
}> = [
  {
    id: 'NONE',
    name: ''
  },
  {
    id: 'RALLY_MARCH',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Rally / March')
  },
  {
    id: 'FUNDRAISER',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Fundraiser')
  },
  {
    id: 'OPEN_HOUSE',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Open House')
  },
  {
    id: 'CONFERENCE',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Conference')
  },
  {
    id: 'WEBINAR',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Webinar')
  },
  {
    id: 'WORKSHOP',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Workshop')
  },
  {
    id: 'OTHER',
    name: /* TRANSLATORS: "Volunteer +" type */ getText('Other Opportunity')
  }
];

export const VOLOP_IMPORTED_TYPE_MAP: {
  [VolopImportedType]: string
} = {
  DONORS_CHOOSE: 'DonorsChoose.org',
  KIVA: 'Kiva',
  NYCPARKS: 'NYC Parks',
  GLOBAL_GIVING: 'GlobalGiving',
  VOLUNTEERMATCH: 'VolunteerMatch',
  MOVE_ON: 'MoveOn',
  POINTS_OF_LIGHT: 'Points of Light',
  AARP: 'AARP'
};

export const VOLOP_SOURCES = {
  IDEALIST: 'Idealist',
  ...VOLOP_IMPORTED_TYPE_MAP
};

export const VOLOP_SOURCES_LINKS = {
  NONE: getText('All Sources'),
  ...VOLOP_SOURCES
};

export const VOLOP_SOURCE_FOR_ACTION_TYPE = {
  NONE: [...Object.keys(VOLOP_SOURCES)],
  VOLOP: ['NONE', 'IDEALIST', 'VOLUNTEERMATCH', 'POINTS_OF_LIGHT'],
  EVENT: ['NONE', 'IDEALIST', 'NYCPARKS'],
  LOAN: ['KIVA'],
  DONATION: ['NONE', 'DONORS_CHOOSE', 'GLOBAL_GIVING'],
  OTHER: ['IDEALIST'],
  PETITION: ['MOVE_ON']
};

export const THIRD_PARTY_TYPE_MAP = {
  GREENHOUSE: 'Greenhouse',
  DOCTORS_WITHOUT_BORDERS: 'DWB/MSF Integration'
};

export const CREDIT_PACKS = [
  {
    id: 'JOB_PACK_10',
    quantity: 10,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_20',
    quantity: 20,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_25',
    quantity: 25,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_30',
    quantity: 30,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_50',
    quantity: 50,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_100',
    quantity: 100,
    type: 'JOB'
  },
  {
    id: 'JOB_PACK_500',
    quantity: 500,
    type: 'JOB'
  },
  {
    id: 'INTERNSHIP_PACK_10',
    quantity: 10,
    type: 'INTERNSHIP'
  }
];

export const PAYMENT_TYPES_STAFF = [
  { id: 'CHECK', name: 'Check' },
  { id: 'EFT', name: 'EFT' },
  { id: 'STRIPE', name: 'Stripe' },
  { id: 'LEGACY_CARD', name: 'Authorize.net / Paypal (legacy)' },
  { id: 'CREDIT', name: 'Job/Internship/Grad Program/Fair Credit' },
  { id: 'PROMO_CREDIT', name: 'Promo Credit' },
  { id: 'VOID', name: 'Void' },
  { id: 'LEGACY_BUG', name: 'Unknown' }
];

export const STAFF_INVOICE_ACTIVITY_ACTION_TYPES = [
  { id: 'POSTED', name: 'Posted' },
  { id: 'PAID', name: 'Paid' }
];
export const PAYMENT_TYPES = [
  { id: 'CHECK', name: getText('Check') },
  { id: 'EFT', name: getText('EFT') },
  { id: 'STRIPE', name: getText('Credit Card') },
  { id: 'LEGACY_CARD', name: getText('Credit Card') },
  { id: 'CREDIT', name: getText('Job/Internship Credit') },
  { id: 'PROMO_CREDIT', name: 'Promo Credit' },
  { id: 'VOID', name: getText('Void') },
  { id: 'LEGACY_BUG', name: getText('Unknown') }
];

export const LISTING_FLAGGING_REASONS = [
  { id: 'MISSING_INFORMATION', description: getText('Missing information') },
  {
    id: 'THIS_IS_A_SCAM_SPAM',
    description: getText('This is a scam/spam')
  },
  {
    id: 'THIS_IS_ILLEGAL_INCITES_HATE',
    description: getText('This is illegal/incites hate')
  },
  {
    id: 'LISTING_IS_A_DUPLICATE',
    description: getText('Listing is a duplicate')
  },
  {
    id: 'LOCATION_IS_INCORRECT',
    description: getText('Location is incorrect')
  },
  {
    id: 'INFORMATION_IS_OUT_OF_DATE',
    description: getText('Information is out of date')
  },
  {
    id: 'LISTING_IS_LOW_QUALITY',
    description: getText('Listing is low quality')
  },
  { id: 'WRONG_CATEGORY', description: getText('Wrong category') },
  { id: 'OTHER', description: getText('Other') }
];

export const ORG_FLAGGING_REASONS = [
  { id: 'MISSING_INFORMATION', description: getText('Missing information') },
  {
    id: 'THIS_IS_A_SCAM_SPAM',
    description: getText('This is a scam/spam')
  },
  {
    id: 'THIS_IS_ILLEGAL_INCITES_HATE',
    description: getText('This is illegal/incites hate')
  },
  {
    id: 'ORGANIZATION_POSTS_MANY_DUPLICATES',
    description: getText('Organization posts many duplicates')
  },
  {
    id: 'INFORMATION_IS_OUT_OF_DATE',
    description: getText('Information is out of date')
  },
  {
    id: 'WRONG_CATEGORY',
    description: getText('Wrong category')
  },
  { id: 'OTHER', description: getText('Other') }
];

export const GROUP_FLAGGING_REASONS = [
  { id: 'MISSING_INFORMATION', description: getText('Missing information') },
  {
    id: 'THIS_IS_A_SCAM_SPAM',
    description: getText('This is a scam/spam')
  },
  {
    id: 'THIS_IS_ILLEGAL_INCITES_HATE',
    description: getText('This is illegal/incites hate')
  },
  {
    id: 'INFORMATION_IS_OUT_OF_DATE',
    description: getText('Information is out of date')
  },
  { id: 'OTHER', description: getText('Other') }
];

export const DAYS_MAP_FLAGGING_REASONS = [
  {
    id: 'IRRELEVANT_INAPPROPRIATE',
    description: getText('This is irrelevant/inappropriate')
  },
  {
    id: 'THIS_IS_A_SCAM_SPAM',
    description: getText('This is a scam/spam')
  },
  {
    id: 'THIS_IS_ILLEGAL_INCITES_HATE',
    description: getText('This is illegal/incites hate')
  },
  { id: 'OTHER', description: getText('Other') }
];

export const USER_FLAGGING_REASONS = [
  {
    id: 'SCAM_SPAM',
    description: getText('Content shared is spam/a scam')
  },
  {
    id: 'ABUSIVE_HARASSING',
    description: getText('Content shared is abusive/harassing')
  },
  { id: 'OTHER', description: getText('Other') }
];

export const FLAGGING_REASONS = {
  ORG: ORG_FLAGGING_REASONS,
  GRAD_PROGRAM: ORG_FLAGGING_REASONS,
  GROUP: GROUP_FLAGGING_REASONS,
  IDEALIST_DAYS_MAP_PIN: DAYS_MAP_FLAGGING_REASONS,
  JOB: LISTING_FLAGGING_REASONS,
  INTERNSHIP: LISTING_FLAGGING_REASONS,
  VOLOP: LISTING_FLAGGING_REASONS,
  IDEALIST_DAY_EVENT: LISTING_FLAGGING_REASONS,
  USER: USER_FLAGGING_REASONS
};

export const ACTION_LOG_ACTIONS = {
  ORG: {
    CREATED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created organization {object}
      </span>
    ),
    APPROVED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} approved organization {object}
      </span>
    ),
    REJECTED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} rejected organization {object}
      </span>
    ),
    DELETED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted organization {object}
      </span>
    ),
    SPAMMED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked organization {object} as spam
      </span>
    ),
    EDITED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited organization {object}
      </span>
    ),
    ADDED_ADMIN: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added administrator to {object}
      </span>
    ),
    ADDED_OWNER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added owner to {object}
      </span>
    ),
    REMOVED_ADMIN: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} removed administrator from {object}
      </span>
    ),
    TRANSFERED_OWNERSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} transfered ownership of {object}
      </span>
    ),
    CHANGED_ORG_TYPE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} changed organization type of {object}
      </span>
    ),
    DEFERRED_NEW_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deferred new organization {object}
      </span>
    ),
    INCLUDED_IN_FEEDS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} included {object} listings in feeds
      </span>
    ),
    EXCLUDED_FROM_FEEDS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} excluded {object} listings from feeds
      </span>
    ),
    MERGED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} merged another organization into {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    REVIEWED_FROZEN_ORG_PAYMENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed frozen org payment queue item for {object}
      </span>
    ),
    REVIEWED_VAT_NUMBER_CHANGE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed VAT number changed queue item for {object}
      </span>
    ),
    REVIEWED_LATE_PAYMENT: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} reviewed late payment queue item for {object}
      </span>
    ),
    REVIEWED_OVERPOSTER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} reviewed overposter queue item for {object}
      </span>
    ),
    ALLOWED_CHECKS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} allowed {object} to pay by check
      </span>
    ),
    DISALLOWED_CHECKS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} disallowed {object} to pay by check
      </span>
    ),
    ALLOWED_JAZZHR: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} allowed {object} to use JazzHR
      </span>
    ),
    DISALLOWED_JAZZHR: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} disallowed {object} to use JazzHR
      </span>
    ),
    ALLOWED_GREENHOUSE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} allowed {object} to use Greenhouse
      </span>
    ),
    DISALLOWED_GREENHOUSE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} disallowed {object} to use Greenhouse
      </span>
    ),
    ALLOWED_ANONYMOUS_POSTING: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} allowed {object} to post anonymously
      </span>
    ),
    DISALLOWED_ANONYMOUS_POSTING: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} disallowed {object} to post anonymously
      </span>
    ),
    FROZE_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} froze {object}
      </span>
    ),
    UNFROZE_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unfroze {object}
      </span>
    ),
    MIGRATED_ORG: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    ),
    GAVE_STAGE_TWO: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave grad fairs stage 2 privileges to {object}
      </span>
    ),
    GAVE_FAIR_PERM_OVERRIDE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave grad fairs permission override to {object}
      </span>
    ),
    REMOVED_FAIR_PERM_OVERRIDE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} removed grad fairspermission override from {object}
      </span>
    ),
    ALLOWED_API: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} allowed API access to {object}
      </span>
    ),
    DISALLOWED_API: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} disallowed API access to {object}
      </span>
    ),
    ALLOWED_OVERPOST: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} allowed overposting for {object}
      </span>
    ),
    DISALLOWED_OVERPOST: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} disallowed overposting for {object}
      </span>
    )
  },
  USER: {
    CREATED_USER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created user {object}
      </span>
    ),
    GAVE_OWNER_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave org owner privileges to {object}
      </span>
    ),
    GAVE_ADMIN_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave org administrator privileges to {object}
      </span>
    ),
    REMOVED_ADMIN_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} removed org administrator privileges {object}
      </span>
    ),
    GAVE_GROUP_OWNER_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave group owner privileges to {object}
      </span>
    ),
    GAVE_GROUP_ADMIN_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave group administrator privileges to {object}
      </span>
    ),
    REMOVED_GROUP_ADMIN_PRIV: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} removed group administrator privileges {object}
      </span>
    ),
    GAVE_STAFF_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} gave staff privileges to {object}
      </span>
    ),
    REMOVED_STAFF_PRIV: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} removed staff privileges from {object}
      </span>
    ),
    UPDATED_GENERAL_SETTINGS: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} updated general settings of {object}
      </span>
    ),
    UPDATED_USER_PROFILE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} updated user profile of {object}
      </span>
    ),
    UPDATED_EMAIL: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} updated email of {object}
      </span>
    ),
    MARK_EMAIL_VERIFIED: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked email address of {object} as verified
      </span>
    ),
    UPDATED_NOTIFICATION_PREFERENCES: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} updated notification preferences of {object}
      </span>
    ),
    MARK_EMAIL_UNVERIFIED: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked email address of {object} as unverified
      </span>
    ),
    MARK_RECRUITABLE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked {object} as recruitable
      </span>
    ),
    MARK_NOT_RECRUITABLE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked {object} as not recruitable
      </span>
    ),
    MIGRATED_USER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    ),
    DELETED_USER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted user {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    )
  },
  JOB: {
    CREATED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created {object}
      </span>
    ),
    EDITED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited {object}
      </span>
    ),
    PUBLISHED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} published {object}
      </span>
    ),
    UNPUBLISHED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unpublished {object}
      </span>
    ),
    MARKED_JOB_READY_TO_PUBLISH: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} marked {object} as ready to publish
      </span>
    ),
    COMPLETED_CHANGES_REVIEW: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed changes of {object}
      </span>
    ),
    FROZE_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} froze {object}
      </span>
    ),
    UNFROZE_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unfroze {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    HID_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} hid {object}
      </span>
    ),
    UNHID_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unhid {object}
      </span>
    ),
    ADDED_JOB_ON_INVOICE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added {object} on an invoice
      </span>
    ),
    REMOVED_JOB_FROM_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} removed {object} from an invoice
      </span>
    ),
    DELETED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted job {object}
      </span>
    ),
    MIGRATED_JOB: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    )
  },
  INTERNSHIP: {
    CREATED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created {object}
      </span>
    ),
    EDITED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited {object}
      </span>
    ),
    PUBLISHED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} published {object}
      </span>
    ),
    UNPUBLISHED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unpubished {object}
      </span>
    ),
    MARKED_INTERNSHIP_READY_TO_PUBLISH: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} marked {object} as ready to publish
      </span>
    ),
    COMPLETED_CHANGES_REVIEW: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed changes of {object}
      </span>
    ),
    FROZE_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} froze {object}
      </span>
    ),
    UNFROZE_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unfroze {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    HID_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} hid {object}
      </span>
    ),
    UNHID_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unhid {object}
      </span>
    ),
    ADDED_INTERNSHIP_ON_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} added {object} on an invoice
      </span>
    ),
    REMOVED_INTERNSHIP_FROM_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} removed {object} from an invoice
      </span>
    ),
    DELETED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted internship {object}
      </span>
    ),
    MIGRATED_INTERNSHIP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    )
  },
  VOLOP: {
    CREATED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created {object}
      </span>
    ),
    EDITED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited {object}
      </span>
    ),
    PUBLISHED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} published {object}
      </span>
    ),
    UNPUBLISHED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unpublished {object}
      </span>
    ),
    MARKED_VOLOP_READY_TO_PUBLISH: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} marked {object} as ready to publish
      </span>
    ),
    COMPLETED_AS_INTERNSHIP_REVIEW: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed internship wording of {object}
      </span>
    ),
    FROZE_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} froze {object}
      </span>
    ),
    UNFROZE_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unfroze {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    HID_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} hid {object}
      </span>
    ),
    UNHID_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unhid {object}
      </span>
    ),
    DELETED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted volop {object}
      </span>
    ),
    MIGRATED_VOLOP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    )
  },
  INVOICE: {
    CREATED_INVOICE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created {object}
      </span>
    ),
    ADDED_JOB_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added job item to {object}
      </span>
    ),
    ADDED_INTERNSHIP_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added internship item to {object}
      </span>
    ),
    ADDED_CREDIT_PACK_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added credit pack item to {object}
      </span>
    ),
    ADDED_GRAD_PROGRAM_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added grad program item to {object}
      </span>
    ),
    ADDED_DONATION_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added donation item to {object}
      </span>
    ),
    ADDED_FAIR_ITEMS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added grad fair items to {object}
      </span>
    ),
    WILL_PAY_BY_CHECK: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} will pay {object} by check
      </span>
    ),
    APPLIED_PAYMENT: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} applied a payment to {object}
      </span>
    ),
    REVERTED_PAYMENT: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} reverted a payment previously applied to {object}
      </span>
    ),
    PAID_CHECKOUT_BY_CC: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} paid {object} by credit card during checkout
      </span>
    ),
    PAID_AFTER_CHECKOUT_BY_CC: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} paid {object} by credit card after checkout
      </span>
    ),
    USED_CREDITS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} used credits on {object}
      </span>
    ),
    VOIDED_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} voided items on {object}
      </span>
    ),
    REFUNDED_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} refunded items on {object}
      </span>
    ),
    ADDED_ITEMS_FROM_OTHER_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} added items on {object} from another invoice
      </span>
    ),
    REMOVED_ITEM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} removed an item from {object}
      </span>
    ),
    MIGRATED_INVOICE: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    ),
    ALLOWED_PARTIAL_PAYMENTS: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} started accepting partial payments for {object}
      </span>
    ),
    DISALLOWED_PARTIAL_PAYMENTS: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} stopped accepting partial payments for {object}
      </span>
    ),
    CHANGED_BILLING_ADDRESS: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} changed the billing address of {object}
      </span>
    ),
    CHECKOUT_BY_APPROVING_ORG: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} completed checkout by approving org of {object}
      </span>
    )
  },
  CREDIT_PACK: {
    CANCELLED_CREDIT: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} cancelled a credit from {object}
      </span>
    )
  },
  GROUP: {
    CREATED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created group {object}
      </span>
    ),
    EDITED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited group {object}
      </span>
    ),
    APPROVED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} approved group {object}
      </span>
    ),
    REJECTED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} rejected group {object}
      </span>
    ),
    SPAMMED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} marked group {object} as spam
      </span>
    ),
    DEFERRED_NEW_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deferred group {object}
      </span>
    ),
    ADDED_GROUP_OWNER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added owner to {object}
      </span>
    ),
    ADDED_GROUP_ADMIN: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} added administrator to {object}
      </span>
    ),
    REMOVED_GROUP_ADMIN: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} removed administrator from {object}
      </span>
    ),
    FROZE_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} froze group {object}
      </span>
    ),
    UNFROZE_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unfroze group {object}
      </span>
    ),
    DELETED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} deleted group {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    REVIEWED_OVERPOSTER: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} reviewed overposter queue item for {object}
      </span>
    ),
    MERGED_GROUP: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} merged another group into {object}
      </span>
    )
  },
  IDEALIST_DAY_EVENT: {
    CREATED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} created Idealist Day event {object}
      </span>
    ),
    EDITED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} edited Idealist Day event {object}
      </span>
    ),
    PUBLISHED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} published Idealist Day event {object}
      </span>
    ),
    UNPUBLISHED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} unpublished Idealist Day event {object}
      </span>
    ),
    MARKED_IDEALIST_DAY_EVENT_READY_TO_PUBLISH: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} marked {object} as ready to publish
      </span>
    ),
    DELETED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} deleted Idealist Day event from {object}
      </span>
    ),
    FROZE_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} froze Idealist Day event {object}
      </span>
    ),
    UNFROZE_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} unfroze Idealist Day event {object}
      </span>
    ),
    HID_IDEALIST_DAY_EVENT: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} hid Idealist Day event {object}
      </span>
    ),
    UNHID_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} unhid Idealist Day event {object}
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    APPROVED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} approved Idealist Day event {object}
      </span>
    ),
    REJECTED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} rejected Idealist Day event {object}
      </span>
    ),
    DEFERRED_NEW_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} deferred Idealist Day event {object}
      </span>
    ),
    MIGRATED_IDEALIST_DAY_EVENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    )
  },
  GRAD_PROGRAM: {
    CREATED_GRAD_PRORGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} created {object}
      </span>
    ),
    EDITED_GRAD_PRORGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} edited {object}
      </span>
    ),
    ADDED_GRAD_PROGRAM_ON_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} added {object} on an invoice
      </span>
    ),
    REMOVED_GRAD_PROGRAM_FROM_INVOICE: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} removed {object} from an invoice
      </span>
    ),
    REVIEWED_FLAGGED_CONTENT: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed flagged content {object}
      </span>
    ),
    PUBLISHED_GRAD_PROGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} published {object}
      </span>
    ),
    UNPUBLISHED_GRAD_PROGRAM: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} unpublished {object}
      </span>
    ),
    ADDED_ENTITLEMENT_TO_GRAD_PROGRAM: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} added entitlement to {object}
      </span>
    ),
    MARKED_GRAD_PROGRAM_READY_TO_PUBLISH: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed {object} as ready to publish
      </span>
    ),
    REVIEWED_NEW_GRAD_PROGRAM: ({
      user,
      object
    }: {
      user: any,
      object: any
    }) => (
      <span>
        {user} reviewed grad program {object}
      </span>
    ),
    HID_GRAD_PROGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} hid {object}
      </span>
    ),
    UNHID_GRAD_PROGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} unhid {object}
      </span>
    ),
    MIGRATED_GRAD_PROGRAM: ({ user, object }: { user: any, object: any }) => (
      <span>
        {user} migrated the data of {object}
      </span>
    )
  },
  NONE: {}
};

export const US_STATES = [
  { id: 'AL', name: getText('Alabama') },
  { id: 'AK', name: getText('Alaska') },
  { id: 'AS', name: getText('American Samoa') },
  { id: 'AZ', name: getText('Arizona') },
  { id: 'AR', name: getText('Arkansas') },
  { id: 'CA', name: getText('California') },
  { id: 'CO', name: getText('Colorado') },
  { id: 'CT', name: getText('Connecticut') },
  { id: 'DE', name: getText('Delaware') },
  { id: 'DC', name: getText('District of Columbia') },
  { id: 'FM', name: getText('Federated States of Micronesia') },
  { id: 'FL', name: getText('Florida') },
  { id: 'GA', name: getText('Georgia') },
  { id: 'GU', name: getText('Guam') },
  { id: 'HI', name: getText('Hawaii') },
  { id: 'ID', name: getText('Idaho') },
  { id: 'IL', name: getText('Illinois') },
  { id: 'IN', name: getText('Indiana') },
  { id: 'IA', name: getText('Iowa') },
  { id: 'KS', name: getText('Kansas') },
  { id: 'KY', name: getText('Kentucky') },
  { id: 'LA', name: getText('Louisiana') },
  { id: 'ME', name: getText('Maine') },
  { id: 'MH', name: getText('Marshall Islands') },
  { id: 'MD', name: getText('Maryland') },
  { id: 'MA', name: getText('Massachusetts') },
  { id: 'MI', name: getText('Michigan') },
  { id: 'MN', name: getText('Minnesota') },
  { id: 'MS', name: getText('Mississippi') },
  { id: 'MO', name: getText('Missouri') },
  { id: 'MT', name: getText('Montana') },
  { id: 'NE', name: getText('Nebraska') },
  { id: 'NV', name: getText('Nevada') },
  { id: 'NH', name: getText('New Hampshire') },
  { id: 'NJ', name: getText('New Jersey') },
  { id: 'NM', name: getText('New Mexico') },
  { id: 'NY', name: getText('New York') },
  { id: 'NC', name: getText('North Carolina') },
  { id: 'ND', name: getText('North Dakota') },
  { id: 'MP', name: getText('Northern Mariana Islands') },
  { id: 'OH', name: getText('Ohio') },
  { id: 'OK', name: getText('Oklahoma') },
  { id: 'OR', name: getText('Oregon') },
  { id: 'PW', name: getText('Palau') },
  { id: 'PA', name: getText('Pennsylvania') },
  { id: 'PR', name: getText('Puerto Rico') },
  { id: 'RI', name: getText('Rhode Island') },
  { id: 'SC', name: getText('South Carolina') },
  { id: 'SD', name: getText('South Dakota') },
  { id: 'TN', name: getText('Tennessee') },
  { id: 'TX', name: getText('Texas') },
  { id: 'UT', name: getText('Utah') },
  { id: 'VT', name: getText('Vermont') },
  { id: 'VI', name: getText('Virgin Islands') },
  { id: 'VA', name: getText('Virginia') },
  { id: 'WA', name: getText('Washington') },
  { id: 'WV', name: getText('West Virginia') },
  { id: 'WI', name: getText('Wisconsin') },
  { id: 'WY', name: getText('Wyoming') }
];

const UNSORTED_COUNTRIES = [
  { id: 'AF', name: getText('Afghanistan') },
  { id: 'AX', name: getText('Åland') },
  { id: 'AL', name: getText('Albania') },
  { id: 'DZ', name: getText('Algeria') },
  { id: 'AS', name: getText('American Samoa') },
  { id: 'AD', name: getText('Andorra') },
  { id: 'AO', name: getText('Angola') },
  { id: 'AI', name: getText('Anguilla') },
  { id: 'AQ', name: getText('Antarctica') },
  { id: 'AG', name: getText('Antigua and Barbuda') },
  { id: 'AR', name: getText('Argentina') },
  { id: 'AM', name: getText('Armenia') },
  { id: 'AW', name: getText('Aruba') },
  { id: 'AU', name: getText('Australia') },
  { id: 'AT', name: getText('Austria') },
  { id: 'AZ', name: getText('Azerbaijan') },
  { id: 'BS', name: getText('Bahamas') },
  { id: 'BH', name: getText('Bahrain') },
  { id: 'BD', name: getText('Bangladesh') },
  { id: 'BB', name: getText('Barbados') },
  { id: 'BY', name: getText('Belarus') },
  { id: 'BE', name: getText('Belgium') },
  { id: 'BZ', name: getText('Belize') },
  { id: 'BJ', name: getText('Benin') },
  { id: 'BM', name: getText('Bermuda') },
  { id: 'BT', name: getText('Bhutan') },
  { id: 'BO', name: getText('Bolivia') },
  { id: 'BQ', name: getText('Bonaire') },
  { id: 'BA', name: getText('Bosnia and Herzegovina') },
  { id: 'BW', name: getText('Botswana') },
  { id: 'BV', name: getText('Bouvet Island') },
  { id: 'BR', name: getText('Brazil') },
  { id: 'IO', name: getText('British Indian Ocean Territory') },
  { id: 'VG', name: getText('British Virgin Islands') },
  { id: 'BN', name: getText('Brunei') },
  { id: 'BG', name: getText('Bulgaria') },
  { id: 'BF', name: getText('Burkina Faso') },
  { id: 'BI', name: getText('Burundi') },
  { id: 'KH', name: getText('Cambodia') },
  { id: 'CM', name: getText('Cameroon') },
  { id: 'CA', name: getText('Canada') },
  { id: 'CV', name: getText('Cape Verde') },
  { id: 'KY', name: getText('Cayman Islands') },
  { id: 'CF', name: getText('Central African Republic') },
  { id: 'TD', name: getText('Chad') },
  { id: 'CL', name: getText('Chile') },
  { id: 'CN', name: getText('China') },
  { id: 'CX', name: getText('Christmas Island') },
  { id: 'CC', name: getText('Cocos [Keeling] Islands') },
  { id: 'CO', name: getText('Colombia') },
  { id: 'KM', name: getText('Comoros') },
  { id: 'CK', name: getText('Cook Islands') },
  { id: 'CR', name: getText('Costa Rica') },
  { id: 'HR', name: getText('Croatia') },
  { id: 'CU', name: getText('Cuba') },
  { id: 'CW', name: getText('Curacao') },
  { id: 'CY', name: getText('Cyprus') },
  { id: 'CZ', name: getText('Czechia') },
  { id: 'CD', name: getText('Democratic Republic of the Congo') },
  { id: 'DK', name: getText('Denmark') },
  { id: 'DJ', name: getText('Djibouti') },
  { id: 'DM', name: getText('Dominica') },
  { id: 'DO', name: getText('Dominican Republic') },
  { id: 'TL', name: getText('East Timor') },
  { id: 'EC', name: getText('Ecuador') },
  { id: 'EG', name: getText('Egypt') },
  { id: 'SV', name: getText('El Salvador') },
  { id: 'GQ', name: getText('Equatorial Guinea') },
  { id: 'ER', name: getText('Eritrea') },
  { id: 'EE', name: getText('Estonia') },
  { id: 'ET', name: getText('Ethiopia') },
  { id: 'FK', name: getText('Falkland Islands') },
  { id: 'FO', name: getText('Faroe Islands') },
  { id: 'FJ', name: getText('Fiji') },
  { id: 'FI', name: getText('Finland') },
  { id: 'FR', name: getText('France') },
  { id: 'GF', name: getText('French Guiana') },
  { id: 'PF', name: getText('French Polynesia') },
  { id: 'TF', name: getText('French Southern Territories') },
  { id: 'GA', name: getText('Gabon') },
  { id: 'GM', name: getText('Gambia') },
  { id: 'GE', name: getText('Georgia') },
  { id: 'DE', name: getText('Germany') },
  { id: 'GH', name: getText('Ghana') },
  { id: 'GI', name: getText('Gibraltar') },
  { id: 'GR', name: getText('Greece') },
  { id: 'GL', name: getText('Greenland') },
  { id: 'GD', name: getText('Grenada') },
  { id: 'GP', name: getText('Guadeloupe') },
  { id: 'GU', name: getText('Guam') },
  { id: 'GT', name: getText('Guatemala') },
  { id: 'GG', name: getText('Guernsey') },
  { id: 'GN', name: getText('Guinea') },
  { id: 'GW', name: getText('Guinea-Bissau') },
  { id: 'GY', name: getText('Guyana') },
  { id: 'HT', name: getText('Haiti') },
  { id: 'HM', name: getText('Heard Island and McDonald Islands') },
  { id: 'HN', name: getText('Honduras') },
  { id: 'HK', name: getText('Hong Kong') },
  { id: 'HU', name: getText('Hungary') },
  { id: 'IS', name: getText('Iceland') },
  { id: 'IN', name: getText('India') },
  { id: 'ID', name: getText('Indonesia') },
  { id: 'IR', name: getText('Iran') },
  { id: 'IQ', name: getText('Iraq') },
  { id: 'IE', name: getText('Ireland') },
  { id: 'IM', name: getText('Isle of Man') },
  { id: 'IL', name: getText('Israel') },
  { id: 'IT', name: getText('Italy') },
  { id: 'CI', name: getText('Ivory Coast') },
  { id: 'JM', name: getText('Jamaica') },
  { id: 'JP', name: getText('Japan') },
  { id: 'JE', name: getText('Jersey') },
  { id: 'JO', name: getText('Jordan') },
  { id: 'KZ', name: getText('Kazakhstan') },
  { id: 'KE', name: getText('Kenya') },
  { id: 'KI', name: getText('Kiribati') },
  { id: 'XK', name: getText('Kosovo') },
  { id: 'KW', name: getText('Kuwait') },
  { id: 'KG', name: getText('Kyrgyzstan') },
  { id: 'LA', name: getText('Laos') },
  { id: 'LV', name: getText('Latvia') },
  { id: 'LB', name: getText('Lebanon') },
  { id: 'LS', name: getText('Lesotho') },
  { id: 'LR', name: getText('Liberia') },
  { id: 'LY', name: getText('Libya') },
  { id: 'LI', name: getText('Liechtenstein') },
  { id: 'LT', name: getText('Lithuania') },
  { id: 'LU', name: getText('Luxembourg') },
  { id: 'MO', name: getText('Macao') },
  { id: 'MK', name: getText('Macedonia') },
  { id: 'MG', name: getText('Madagascar') },
  { id: 'MW', name: getText('Malawi') },
  { id: 'MY', name: getText('Malaysia') },
  { id: 'MV', name: getText('Maldives') },
  { id: 'ML', name: getText('Mali') },
  { id: 'MT', name: getText('Malta') },
  { id: 'MH', name: getText('Marshall Islands') },
  { id: 'MQ', name: getText('Martinique') },
  { id: 'MR', name: getText('Mauritania') },
  { id: 'MU', name: getText('Mauritius') },
  { id: 'YT', name: getText('Mayotte') },
  { id: 'MX', name: getText('Mexico') },
  { id: 'FM', name: getText('Micronesia') },
  { id: 'MD', name: getText('Moldova') },
  { id: 'MC', name: getText('Monaco') },
  { id: 'MN', name: getText('Mongolia') },
  { id: 'ME', name: getText('Montenegro') },
  { id: 'MS', name: getText('Montserrat') },
  { id: 'MA', name: getText('Morocco') },
  { id: 'MZ', name: getText('Mozambique') },
  { id: 'MM', name: getText('Myanmar [Burma]') },
  { id: 'NA', name: getText('Namibia') },
  { id: 'NR', name: getText('Nauru') },
  { id: 'NP', name: getText('Nepal') },
  { id: 'NL', name: getText('Netherlands') },
  { id: 'NC', name: getText('New Caledonia') },
  { id: 'NZ', name: getText('New Zealand') },
  { id: 'NI', name: getText('Nicaragua') },
  { id: 'NE', name: getText('Niger') },
  { id: 'NG', name: getText('Nigeria') },
  { id: 'NU', name: getText('Niue') },
  { id: 'NF', name: getText('Norfolk Island') },
  { id: 'KP', name: getText('North Korea') },
  { id: 'MP', name: getText('Northern Mariana Islands') },
  { id: 'NO', name: getText('Norway') },
  { id: 'OM', name: getText('Oman') },
  { id: 'PK', name: getText('Pakistan') },
  { id: 'PW', name: getText('Palau') },
  { id: 'PS', name: getText('Palestine') },
  { id: 'PA', name: getText('Panama') },
  { id: 'PG', name: getText('Papua New Guinea') },
  { id: 'PY', name: getText('Paraguay') },
  { id: 'PE', name: getText('Peru') },
  { id: 'PH', name: getText('Philippines') },
  { id: 'PN', name: getText('Pitcairn Islands') },
  { id: 'PL', name: getText('Poland') },
  { id: 'PT', name: getText('Portugal') },
  { id: 'PR', name: getText('Puerto Rico') },
  { id: 'QA', name: getText('Qatar') },
  { id: 'CG', name: getText('Republic of the Congo') },
  { id: 'RE', name: getText('Réunion') },
  { id: 'RO', name: getText('Romania') },
  { id: 'RU', name: getText('Russia') },
  { id: 'RW', name: getText('Rwanda') },
  { id: 'BL', name: getText('Saint Barthélemy') },
  { id: 'SH', name: getText('Saint Helena') },
  { id: 'KN', name: getText('Saint Kitts and Nevis') },
  { id: 'LC', name: getText('Saint Lucia') },
  { id: 'MF', name: getText('Saint Martin') },
  { id: 'PM', name: getText('Saint Pierre and Miquelon') },
  { id: 'VC', name: getText('Saint Vincent and the Grenadines') },
  { id: 'WS', name: getText('Samoa') },
  { id: 'SM', name: getText('San Marino') },
  { id: 'ST', name: getText('São Tomé and Príncipe') },
  { id: 'SA', name: getText('Saudi Arabia') },
  { id: 'SN', name: getText('Senegal') },
  { id: 'RS', name: getText('Serbia') },
  { id: 'SC', name: getText('Seychelles') },
  { id: 'SL', name: getText('Sierra Leone') },
  { id: 'SG', name: getText('Singapore') },
  { id: 'SX', name: getText('Sint Maarten') },
  { id: 'SK', name: getText('Slovakia') },
  { id: 'SI', name: getText('Slovenia') },
  { id: 'SB', name: getText('Solomon Islands') },
  { id: 'SO', name: getText('Somalia') },
  { id: 'ZA', name: getText('South Africa') },
  { id: 'GS', name: getText('South Georgia and the South Sandwich Islands') },
  { id: 'KR', name: getText('South Korea') },
  { id: 'SS', name: getText('South Sudan') },
  { id: 'ES', name: getText('Spain') },
  { id: 'LK', name: getText('Sri Lanka') },
  { id: 'SD', name: getText('Sudan') },
  { id: 'SR', name: getText('Suriname') },
  { id: 'SJ', name: getText('Svalbard and Jan Mayen') },
  { id: 'SZ', name: getText('Swaziland') },
  { id: 'SE', name: getText('Sweden') },
  { id: 'CH', name: getText('Switzerland') },
  { id: 'SY', name: getText('Syria') },
  { id: 'TW', name: getText('Taiwan') },
  { id: 'TJ', name: getText('Tajikistan') },
  { id: 'TZ', name: getText('Tanzania') },
  { id: 'TH', name: getText('Thailand') },
  { id: 'TG', name: getText('Togo') },
  { id: 'TK', name: getText('Tokelau') },
  { id: 'TO', name: getText('Tonga') },
  { id: 'TT', name: getText('Trinidad and Tobago') },
  { id: 'TN', name: getText('Tunisia') },
  { id: 'TR', name: getText('Turkey') },
  { id: 'TM', name: getText('Turkmenistan') },
  { id: 'TC', name: getText('Turks and Caicos Islands') },
  { id: 'TV', name: getText('Tuvalu') },
  { id: 'UM', name: getText('U.S. Minor Outlying Islands') },
  { id: 'VI', name: getText('U.S. Virgin Islands') },
  { id: 'UG', name: getText('Uganda') },
  { id: 'UA', name: getText('Ukraine') },
  { id: 'AE', name: getText('United Arab Emirates') },
  { id: 'GB', name: getText('United Kingdom') },
  { id: 'US', name: getText('United States') },
  { id: 'UY', name: getText('Uruguay') },
  { id: 'UZ', name: getText('Uzbekistan') },
  { id: 'VU', name: getText('Vanuatu') },
  { id: 'VA', name: getText('Vatican City') },
  { id: 'VE', name: getText('Venezuela') },
  { id: 'VN', name: getText('Vietnam') },
  { id: 'WF', name: getText('Wallis and Futuna') },
  { id: 'EH', name: getText('Western Sahara') },
  { id: 'YE', name: getText('Yemen') },
  { id: 'ZM', name: getText('Zambia') },
  { id: 'ZW', name: getText('Zimbabwe') }
];

export const COUNTRIES: typeof UNSORTED_COUNTRIES = sortFn(
  UNSORTED_COUNTRIES,
  (a, b) => a.name.localeCompare(b.name, USER_LOCALE)
);

export const LANGUAGES = {
  en: 'English',
  es: 'Español',
  pt: 'Português'
};

export const JOB_FUNCTIONS = {
  ACCOUNTING_FINANCE: getText('Accounting & Finance'),
  ADMINISTRATIVE: getText('Administrative'),
  ADVOCACY: getText('Advocacy'),
  ARTS_PHOTOGRAPHY: getText('Arts & Photography'),
  BUSINESS_DEVELOPMENT: getText('Business Development'),
  CASE_SOCIAL_WORK: getText('Case/Social Work'),
  COMMUNICATIONS: getText('Communications'),
  COMMUNITY_ENGAGEMENT_OUTREACH: getText('Community Engagement/Outreach'),
  COMMUNITY_ORGANIZING: getText('Community Organizing'),
  CONSERVATION: getText('Conservation'),
  COUNSELING: getText('Counseling'),
  CUSTOMER_SERVICE: getText('Customer Service'),
  DATABASE_ADMINISTRATION: getText('Database Administration'),
  DATA_EVALUATION_ANALYSIS: getText('Data Evaluation/Analysis'),
  DEVELOPMENT_FUNDRAISING: getText('Development/Fundraising'),
  DIRECT_SERVICES: getText('Direct Services'),
  EQUIPMENT_FACILITIES: getText('Equipment & Facilities'),
  EVENTS: getText('Events'),
  PUBLIC_RELATIONS: getText('Public Relations'),
  GRANTS_ADMINISTRATION: getText('Grants Administration'),
  GRAPHIC_DESIGN: getText('Graphic Design'),
  HEALTHCARE_PROVIDER_PRACTITIONER: getText('Healthcare Provider/Practitioner'),
  HUMAN_RESOURCES: getText('Human Resources'),
  INVESTIGATIONS: getText('Investigations'),
  LEGAL: getText('Legal'),
  MARKETING: getText('Marketing'),
  OPERATIONS: getText('Operations'),
  PROGRAM_MANAGEMENT: getText('Program Management'),
  RESEARCH: getText('Research'),
  SAFETY_RESOURCE_PROTECTION: getText('Safety & Resource Protection'),
  SOCIAL_MEDIA: getText('Social Media'),
  STRATEGY_PLANNING: getText('Strategy & Planning'),
  SUPPLIES_PROCUREMENT_DISTRIBUTION: getText(
    'Supplies Procurement/Distribution'
  ),
  TEACHING_INSTRUCTION: getText('Teaching & Instruction'),
  TECHNOLOGY_IT: getText('Technology/IT'),
  WRITING_EDITORIAL: getText('Writing/Editorial')
};

export const INTERNSHIP_FUNCTIONS = {
  ACCOUNTING_FINANCE: getText('Accounting & Finance'),
  ADMINISTRATIVE: getText('Administrative'),
  ADVOCACY: getText('Advocacy'),
  BUSINESS_DEVELOPMENT: getText('Business Development'),
  COMMUNITY_ENGAGEMENT_OUTREACH: getText('Community Engagement/Outreach'),
  CONSERVATION: getText('Conservation'),
  DATA_ENTRY_ANALYSIS: getText('Data Entry & Analysis'),
  DEVELOPMENT_FUNDRAISING: getText('Development/Fundraising'),
  EVENTS: getText('Events'),
  GRANT_WRITING: getText('Grant Writing'),
  HUMAN_RESOURCES: getText('Human Resources'),
  LEGAL: getText('Legal'),
  MARKETING_COMMUNICATIONS: getText('Marketing & Communications'),
  OPERATIONS: getText('Operations'),
  PHOTOGRAPHY_MEDIA_DESIGN: getText('Photography, Media & Design'),
  PROGRAM_MANAGEMENT: getText('Program Management'),
  PUBLIC_RELATIONS: getText('Public Relations'),
  RESEARCH: getText('Research'),
  SOCIAL_MEDIA: getText('Social Media'),
  STRATEGY: getText('Strategy'),
  TEACHING_INSTRUCTION: getText('Teaching & Instruction'),
  TECHNOLOGY_IT: getText('Technology/IT'),
  WRITING_JOURNALISM: getText('Writing or Journalism')
};

export const VOLOP_FUNCTIONS = {
  ACCOUNTING_FINANCE: getText('Accounting & Finance'),
  ADMINISTRATIVE_OFFICE_SUPPORT: getText('Administrative/Office Support'),
  ADVOCACY: getText('Advocacy'),
  ANIMAL_CARE: getText('Animal Care'),
  ARCHIVING_DATA_ENTRY: getText('Archiving/Data Entry'),
  ARTS_CRAFTS: getText('Arts & Crafts'),
  BOARD_COMMITTEE: getText('Board/Committee'),
  COMMUNICATIONS_MARKETING: getText('Communications & Marketing'),
  DATA_ANALYSIS: getText('Data Analysis'),
  DESIGN: getText('Design'),
  DISASTER_RELIEF: getText('Disaster Relief'),
  DONATION_DRIVE: getText('Donation Drive'),
  ELDER_CARE: getText('Elder Care'),
  EVENT_SUPPORT: getText('Event Support'),
  FOOD_SERVICE: getText('Food Service'),
  FUNDRAISING: getText('Fundraising'),
  HEALTH_WELLNESS: getText('Health & Wellness'),
  LANGUAGES: getText('Languages'),
  LEGAL: getText('Legal'),
  MENTOR_TUTOR: getText('Mentor/Tutor'),
  MUSIC: getText('Music'),
  PHOTOGRAPHY_VIDEOGRAPHY: getText('Photography/Videography'),
  PHYSICAL_SERVICE: getText('Physical Service (Painting, Farming, etc.)'),
  PROJECT_RESEARCH_STUDY: getText('Project or Research Study'),
  PUBLIC_SPEAKING: getText('Public Speaking'),
  SPORTS_FITNESS_COACHING: getText('Sports & Fitness/Coaching'),
  TECHNOlOGY_SUPPORT_WEB_DESIGN: getText('Technology Support & Web Design')
};

export const SEARCH_PAGE_SIZE = 20;

export const CMS_PAGE_TYPE_MAP = {
  POST: 'Post',
  PAGE: 'Page'
};

const nextDate = date => {
  const newDate = new Date(date);
  if (date.getMonth() === 11) {
    newDate.setFullYear(date.getFullYear() + 1);
    newDate.setMonth(0);
    newDate.setDate(1);
  } else {
    newDate.setMonth(date.getMonth() + 1);
    newDate.setDate(date.getMonth() + 2);
  }
  return newDate;
};

const getIdealistDayEventDates = (limit: number) => {
  let eventDate = new Date();
  eventDate.setDate(eventDate.getMonth() + 1);

  if (Date.now() > eventDate) {
    eventDate = nextDate(eventDate);
  }

  const dates = [];

  while (dates.length < limit) {
    dates.push({
      id: formatDate(eventDate, 'yyyy-MM-dd'),
      name: formatDate(eventDate, 'M/d')
    });
    eventDate = nextDate(eventDate);
  }

  return dates;
};

export const IDEALIST_DAY_EVENT_DATES = getIdealistDayEventDates(6);

export const EMPTY_SEARCH_LOCATION = {
  boundingBoxE: null,
  boundingBoxN: null,
  boundingBoxS: null,
  boundingBoxW: null,
  countryCode: null,
  geoType: null,
  latitude: null,
  longitude: null,
  stateCode: null,
  text: null
};

const JOB_FUNCTIONS_FOR_LANDING_PAGES = Object.keys(JOB_FUNCTIONS).map(
  jobFunction => jobFunction.toLowerCase().replace('_', '-')
);

// Keep in sync with the corresponding value in constants.py
export const AREAS_OF_FOCUS = {
  AGRICULTURE: getText('Agriculture'),
  ANIMALS: getText('Animals'),
  ARTS_MUSIC: getText('Arts & Music'),
  CHILDREN_YOUTH: getText('Children & Youth'),
  CIVIC_ENGAGEMENT: getText('Civic Engagement'),
  CLIMATE_CHANGE: getText('Climate Change'),
  COMMUNICATIONS_ACCESS: getText('Communications Access'),
  COMMUNITY_DEVELOPMENT: getText('Community Development'),
  CONFLICT_RESOLUTION: getText('Conflict Resolution'),
  CONSUMER_PROTECTION: getText('Consumer Protection'),
  CRIME_SAFETY: getText('Crime & Safety'),
  DISABILITY: getText('Disability'),
  DISASTER_RELIEF: getText('Disaster Relief'),
  ECONOMIC_DEVELOPMENT: getText('Economic Development'),
  EDUCATION: getText('Education'),
  ENERGY: getText('Energy'),
  ENTREPRENEURSHIP: getText('Entrepreneurship'),
  ENVIRONMENT: getText('Environment & Sustainability'),
  FAMILY: getText('Family'),
  FINANCIAL_LITERACY_PERSONAL_FINANCE: getText(
    'Financial Literacy & Personal Finance'
  ),
  HEALTH_MEDICINE: getText('Health & Medicine'),
  HOUSING_HOMELESSNESS: getText('Housing & Homelessness'),
  HUMAN_RIGHTS_CIVIL_LIBERTIES: getText('Human Rights & Civil Liberties'),
  HUNGER_FOOD_SECURITY: getText('Hunger, Food Security'),
  IMMIGRANTS_OR_REFUGEES: getText('Immigrants or Refugees'),
  INTERNATIONAL_RELATIONS: getText('International Relations'),
  JOB_WORKPLACE: getText('Job & Workplace'),
  LEGAL_ASSISTANCE: getText('Legal Assistance'),
  LGBTQ: getText('LGBTQ'),
  MEDIA: getText('Media'),
  MEN: getText('Men'),
  MENTAL_HEALTH: getText('Mental Health'),
  MICROFINANCE: getText('Microfinance'),
  PHILANTHROPY: getText('Philanthropy'),
  POLICY: getText('Policy'),
  POVERTY: getText('Poverty'),
  PRISON_REFORM: getText('Prison Reform'),
  RACE_ETHNICITY: getText('Race & Ethnicity'),
  RELIGION_SPIRITUALITY: getText('Religion & Spirituality'),
  REPRODUCTIVE_HEALTH_RIGHTS: getText('Reproductive Health/Rights'),
  RESEARCH_SOCIAL_SCIENCE: getText('Research & Social Science'),
  RURAL_AREAS: getText('Rural Areas'),
  SCIENCE_TECHNOLOGY: getText('Science & Technology'),
  SENIORS_RETIREMENT: getText('Seniors & Retirement'),
  SEXUAL_ABUSE_HUMAN_TRAFFICKING: getText('Sexual Abuse & Human Trafficking'),
  SPORTS_RECREATION: getText('Sports & Recreation'),
  SUBSTANCE_ABUSE_ADDICTION: getText('Substance Abuse & Addiction'),
  TRAVEL_HOSPITALITY: getText('Travel & Hospitality'),
  TRANSPARENCY_OVERSIGHT: getText('Transparency & Oversight'),
  TRANSPORTATION: getText('Transportation'),
  URBAN_AREAS: getText('Urban Areas'),
  VETERANS: getText('Veterans'),
  VICTIM_SUPPORT: getText('Victim Support'),
  VOLUNTEERING: getText('Volunteering'),
  WATER_SANITATION: getText('Water & Sanitation'),
  WOMEN: getText('Women')
};

const AREAS_OF_FOCUS_FOR_LANDING_PAGES = [
  'agriculture',
  'animals',
  'arts-music',
  'children-youth',
  'civic-engagement',
  'climate-change',
  'communications-access',
  'community-development',
  'conflict-resolution',
  'consumer-protection',
  'crime-safety',
  'disability',
  'disaster-relief',
  'economic-development',
  'education',
  'energy',
  'entrepreneurship',
  'environment',
  'family',
  'financial-literacy-personal-finance',
  'health-medicine',
  'housing-homelessness',
  'human-rights-civil-liberties',
  'hunger-food-security',
  'immigrants-or-refugees',
  'international-relations',
  'job-workplace',
  'legal-assistance',
  'lgbtq',
  'media',
  'men',
  'mental-health',
  'microfinance',
  'philanthropy',
  'policy',
  'poverty',
  'prison-reform',
  'race-ethnicity',
  'religion-spirituality',
  'reproductive-health-rights',
  'research-social-science',
  'rural-areas',
  'science-technology',
  'seniors-retirement',
  'sexual-abuse-human-trafficking',
  'sports-recreation',
  'substance-abuse-addiction',
  'travel-hospitality',
  'transparency-oversight',
  'transportation',
  'urban-areas',
  'veterans',
  'victim-support',
  'volunteering',
  'water-sanitation',
  'women'
];

// If you change this, be sure to change the corresponding value is constants.py
export const SEARCH_LANDING_LOCATIONS = [
  'alabama',
  'alaska',
  'albuquerque-nm',
  'arizona',
  'arkansas',
  'auburn-me',
  'austin-tx',
  'baltimore-md',
  'birmingham-al',
  'boise-id',
  'boston-ma',
  'california',
  'chicago-il',
  'colorado',
  'connecticut',
  'dallas-tx',
  'delaware',
  'denver-co',
  'detroit-mi',
  'durham-nc',
  'florida',
  'georgia',
  'greensboro-nc',
  'hawaii',
  'high-point-nc',
  'houston-tx',
  'idaho',
  'illinois',
  'indiana',
  'indianapolis-in',
  'iowa',
  'kansas',
  'kentucky',
  'los-angeles-ca',
  'louisiana',
  'maine',
  'maryland',
  'massachusetts',
  'michigan',
  'milwaukee-wi',
  'minneapolis-mn',
  'minnesota',
  'mississippi',
  'missouri',
  'montana',
  'nashville-tn',
  'nebraska',
  'nevada',
  'new-hampshire',
  'new-jersey',
  'new-mexico',
  'new-york',
  'new-york-ny',
  'north-carolina',
  'north-dakota',
  'oakland-ca',
  'ohio',
  'oklahoma',
  'oklahoma-city-ok',
  'oregon',
  'pennsylvania',
  'philadelphia-pa',
  'pittsburgh-pa',
  'portland-me',
  'raleigh-nc',
  'rhode-island',
  'san-francisco-ca',
  'santa-fe-nm',
  'seattle-wa',
  'south-carolina',
  'south-dakota',
  'st-paul-mn',
  'tennessee',
  'texas',
  'utah',
  'vermont',
  'virginia',
  'washington',
  'washington-dc',
  'west-virginia',
  'winston-salem-nc',
  'wisconsin',
  'wyoming'
];

// If you change this, be sure to change the corresponding value is constants.py
export const VOLOP_LANDING_LOCATIONS = [
  'dallas',
  'toronto',
  'vancouver',
  'short-hills',
  'king-of-prussia',
  'new-york-city',
  'boston',
  'chicago',
  'washington-dc',
  'miami',
  'orlando',
  'houston',
  'scottsdale',
  'las-vegas',
  'costa-mesa',
  'beverly-hills',
  'san-francisco',
  'san-jose'
];

const getLandingPagesForFilterType = (filterList: Array<string>) => {
  const landingPages = [];
  filterList.forEach(filterItem => {
    SEARCH_LANDING_LOCATIONS.forEach(location => {
      landingPages.push(`/${filterItem}-jobs-${location}`);
    });
  });
  return landingPages;
};

const NONPROFIT_JOBS_LANDING_PAGES = SEARCH_LANDING_LOCATIONS.map(
  location => `/${location}/nonprofit-jobs`
);

const JOB_FUNCTIONS_LANDING_PAGES = getLandingPagesForFilterType(
  JOB_FUNCTIONS_FOR_LANDING_PAGES
);

const AREAS_OF_FOCUS_LANDING_PAGES = getLandingPagesForFilterType(
  AREAS_OF_FOCUS_FOR_LANDING_PAGES
);

const VOLOP_LANDING_PAGES = VOLOP_LANDING_LOCATIONS.map(
  location => `/volunteer-${location}`
);
// Make sure this corresponds with the one in constants.py.
const ADDITIONAL_SEARCH_LANDING_PAGES = [
  '/brumadinho',
  '/novaera',
  '/belohorizonte',
  '/get-job-alerts'
];

export const SEARCH_LANDING_ROUTES = [
  ...NONPROFIT_JOBS_LANDING_PAGES,
  ...JOB_FUNCTIONS_LANDING_PAGES,
  ...AREAS_OF_FOCUS_LANDING_PAGES,
  ...ADDITIONAL_SEARCH_LANDING_PAGES,
  ...VOLOP_LANDING_PAGES
];

export const NO_FILTER_SENTINEL = '_NONE_';

export const AREAS_OF_STUDY = {
  // TRANSLATORS: area of study
  ANTHROPOLOGY: getText('Anthropology'),
  // TRANSLATORS: area of study
  ARCHITECTURE_AND_PLANNING: getText('Architecture and Planning'),
  // TRANSLATORS: area of study
  ART_HISTORY: getText('Art History'),
  // TRANSLATORS: area of study
  BIOLOGY: getText('Biology'),
  // TRANSLATORS: area of study
  BUSINESS: getText('Business'),
  // TRANSLATORS: area of study
  CLINICAL_HEALTH: getText('Clinical Health'),
  // TRANSLATORS: area of study
  COMMUNICATION: getText('Communication'),
  // TRANSLATORS: area of study
  COMMUNITY_DEVELOPMENT: getText('Community Development'),
  // TRANSLATORS: area of study
  COMPUTER_SCIENCE: getText('Computer Science'),
  // TRANSLATORS: area of study
  CONFLICT_RESOLUTION: getText('Conflict Resolution'),
  // TRANSLATORS: area of study
  COUNSELING: getText('Counseling'),
  // TRANSLATORS: area of study
  CRIMINOLOGY_AND_CRIMINAL_JUSTICE: getText('Criminology and Criminal Justice'),
  // TRANSLATORS: area of study
  DESIGN: getText('Design'),
  // TRANSLATORS: area of study
  DIGITAL_AND_ELECTRONIC_ARTS: getText('Digital and Electronic Arts'),
  // TRANSLATORS: area of study
  EARTH_SCIENCES: getText('Earth Sciences'),
  // TRANSLATORS: area of study
  ECONOMICS: getText('Economics'),
  // TRANSLATORS: area of study
  EDUCATION: getText('Education'),
  // TRANSLATORS: area of study
  ENGINEERING: getText('Engineering'),
  // TRANSLATORS: area of study
  ENVIRONMENT_AND_SUSTAINABILITY: getText('Environment and Sustainability'),
  // TRANSLATORS: area of study
  GOVERNMENT_AFFAIRS: getText('Government Affairs'),
  // TRANSLATORS: area of study
  HISTORY: getText('History'),
  // TRANSLATORS: area of study
  HUMAN_DEVELOPMENT: getText('Human Development'),
  // TRANSLATORS: area of study
  HUMAN_RESOURCES: getText('Human Resources'),
  // TRANSLATORS: area of study
  INTERDISCIPLINARY_STUDIES: getText('Interdisciplinary Studies'),
  // TRANSLATORS: area of study
  INTERNATIONAL_STUDIES_POLICY_AND_AFFAIRS: getText(
    'International Studies, Policy, and Affairs'
  ),
  // TRANSLATORS: area of study
  JOURNALISM: getText('Journalism'),
  // TRANSLATORS: area of study
  LABOR_RELATIONS: getText('Labor Relations'),
  // TRANSLATORS: area of study
  LAW: getText('Law'),
  // TRANSLATORS: area of study
  LIBRARY_SCIENCE: getText('Library Science'),
  // TRANSLATORS: area of study
  LINGUISTICS: getText('Linguistics'),
  // TRANSLATORS: area of study
  MANAGEMENT: getText('Management'),
  // TRANSLATORS: area of study
  MATHEMATICS_AND_STATISTICS: getText('Mathematics and Statistics'),
  // TRANSLATORS: area of study
  MEDICAL_SCIENCES: getText('Medical Sciences'),
  // TRANSLATORS: area of study
  MUSIC: getText('Music'),
  // TRANSLATORS: area of study
  NONPROFIT_MANAGEMENT: getText('Nonprofit Management'),
  // TRANSLATORS: area of study
  PERFORMING_ARTS: getText('Performing Arts'),
  // TRANSLATORS: area of study
  POLITICAL_SCIENCE: getText('Political Science'),
  // TRANSLATORS: area of study
  PROFESSIONAL_STUDIES: getText('Professional Studies'),
  // TRANSLATORS: area of study
  PSYCHOLOGY: getText('Psychology'),
  // TRANSLATORS: area of study
  PUBLIC_ADMINISTRATION: getText('Public Administration'),
  // TRANSLATORS: area of study
  PUBLIC_AFFAIRS: getText('Public Affairs'),
  // TRANSLATORS: area of study
  PUBLIC_HEALTH: getText('Public Health'),
  // TRANSLATORS: area of study
  PUBLIC_POLICY: getText('Public Policy'),
  // TRANSLATORS: area of study
  RELIGION_AND_THEOLOGY: getText('Religion and Theology'),
  // TRANSLATORS: area of study
  SOCIAL_WORK: getText('Social Work'),
  // TRANSLATORS: area of study
  SOCIAL_SCIENCES: getText('Social Sciences'),
  // TRANSLATORS: area of study
  SOCIOLOGY: getText('Sociology'),
  // TRANSLATORS: area of study
  SPEECH_LANGUAGE_PATHOLOGY: getText('Speech-Language Pathology'),
  // TRANSLATORS: area of study
  THERAPY: getText('Therapy'),
  // TRANSLATORS: area of study
  URBAN_PLANNING: getText('Urban Planning'),
  // TRANSLATORS: area of study
  VISUAL_ARTS: getText('Visual Arts')
};

export type DisplayedType = ListingType | StaffTypeFilter;

export const listingTypesBySection: { [string]: Array<DisplayedType> } = {
  STAFF: [
    'ALL',
    'JOB',
    'INTERNSHIP',
    'VOLOP',
    'ORG',
    'IDEALIST_DAY_EVENT',
    'GROUP',
    'GRAD_PROGRAM',
    'USER',
    'INVOICE',
    'PAYMENT',
    'DONATION'
  ],
  MAIN: ['ALL', 'JOB', 'INTERNSHIP', 'VOLOP', 'ORG', 'GROUP', 'GRAD_PROGRAM']
};

export const INTERNSHIP_PAYMENT_TYPE = {
  // TRANSLATORS: internship payment type
  HOURLY: getText('Hourly'),
  // TRANSLATORS: internship payment type
  SALARY: getText('Salary'),
  // TRANSLATORS: internship payment type
  STIPEND: getText('Stipend')
};

export const IDEALIST_TIME_ZONE = 'America/New_York';

// For tracking sharing listing events
export const getListingTypeHumanName = (listing: any) =>
  LISTING_TYPE_HUMAN_NAME_MAP[listing.type];

export const getListingTypeHumanNameTitle = (listing: any) =>
  LISTING_TYPE_HUMAN_NAME_MAP_TITLE[listing.type];

export const SEARCH_HIT_IMAGE_WIDTH = 120;
export const SEARCH_HIT_IMAGE_WIDTH_SMALL = 92;
